import { Button } from "antd";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SelectedGiftcard } from "src/store/slices/item/subitems/giftcards.types";
import { RootState } from "src/store";
import ResponsiveSheet from "src/components/ResponsiveSheet/ResponsiveSheet";
import GiftcardsRedeemContent from "./layouts/GiftcardsRedeemContent";
import { useOrderItemMutation } from "src/store/slices/item/item.api";
import { track } from "@amplitude/analytics-browser";
import classNames from "classnames";

const REDEEM_MODAL_WIDTH = 520;

type Props = {
  data: SelectedGiftcard | undefined;
  imageUrl: string | undefined;
  itemCode:string;
};

const GiftcardsRedeem: FC<Props> = ({ data, imageUrl, itemCode }) => {
  const { t } = useTranslation();
  const [request, { isLoading, error, data: orderData, reset }] = useOrderItemMutation();
  const pointsBalance = useSelector((state: RootState) => state.client.userDetails.totalPointsBalance);
  const insufficientPoints = (pointsBalance ?? 0) < (data?.points ?? 0);
  const isResultStep = !!(error || orderData);
  const handleConfirm = () => {
    if (!data) return;
    track("Checkout Submitted", { ...data, itemCode });
    request({
      externalTransactionId: `ext-client_-${crypto.randomUUID()}`,
      orderItems: [
        {
          itemCode,
          quantity: 1,
          value: data.totalPrice,
        },
      ],
    });
  };

  useEffect(() => {
    if (!error && !orderData) return;
    track("Checkout Result Viewed", { success: !error, error, orderData, itemCode });
  }, [error, orderData]);

  const handleCheckoutInitiated = () => track("Checkout Initiated", { ...data, itemCode });

  return (
    <ResponsiveSheet
      showTitle={!isResultStep}
      label={
        <div className="flex flex-col">
          <span className="font-semibold">{t("Giftcards.confirmRedemption")}</span>
          <span className="text-font-subtle text-sm font-normal">{t("Giftcards.reviewOrder")}</span>
        </div>
      }
      modalProps={{ width: REDEEM_MODAL_WIDTH, afterClose: reset }}
      drawerProps={{ afterOpenChange: (isOpen) => !isOpen && reset() }}
      renderTrigger={({ open }) => (
        <Button
          size="large"
          block
          disabled={insufficientPoints}
          className={classNames("font-semibold! p-6!", insufficientPoints ? "bg-background-disabled! text-font-disabled!":"bg-background-primary! text-font-inverted!")}
          onClick={() => {
            handleCheckoutInitiated();
            open();
          }}
        >
          {t("Giftcards.redeemNow")}
        </Button>
      )}
      renderSheet={({ close }) => (
        <GiftcardsRedeemContent
          data={data}
          imageUrl={imageUrl}
          onConfirm={handleConfirm}
          onClose={close}
          loading={isLoading}
          error={error}
          orderData={orderData}
          onRetry={reset}
        />
      )}
    />
  );
};

export default GiftcardsRedeem;
